
import { useState, useEffect } from "react";
import { Investor, Contact } from "@/types/investor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InvestorsListView from "./investors/InvestorsListView";
import InvestorsCardView from "./investors/InvestorsCardView";
import InvestorsSearchBar from "./investors/InvestorsSearchBar";
import InvestorsSortControls from "./investors/InvestorsSortControls";
import EnrichmentDebugPanel from "./EnrichmentDebugPanel";
import { enrichmentDebugState } from "@/utils/ai/debugUtils";
import { matchContactsToInvestors } from "@/utils/investorAnalysis";
import { useInvestorFiltering } from "@/hooks/useInvestorFiltering";
import { toast } from "sonner";

interface InvestorsListProps {
  investors: Investor[];
  onSelectInvestor: (investor: Investor) => void;
  selectedInvestor: Investor | null; 
  relevanceContext: string;
  onInvestorUpdate: (updatedInvestor: Investor) => void;
  isEnriching: boolean;
  setIsEnriching: (isEnriching: boolean) => void;
  contacts?: Contact[];
  onProcessInvestor?: (investor: Investor) => Promise<Investor>;
  onEnrichInvestor?: (investor: Investor) => Promise<Investor>;
  isInvestorProcessed?: (investor: Investor) => boolean;
}

const InvestorsList = ({
  investors,
  onSelectInvestor,
  selectedInvestor,
  relevanceContext,
  onInvestorUpdate,
  isEnriching,
  setIsEnriching,
  contacts = [],
  onProcessInvestor,
  onEnrichInvestor,
  isInvestorProcessed
}: InvestorsListProps) => {
  const [syncedInvestors, setSyncedInvestors] = useState<Investor[]>(investors);
  const [enrichingIds, setEnrichingIds] = useState<Set<string>>(new Set());
  const [lastInvestorsLength, setLastInvestorsLength] = useState<number>(investors.length);

  // Check if an investor has been processed - use provided function or fallback
  const checkIfProcessed = (investor: Investor): boolean => {
    if (isInvestorProcessed) {
      return isInvestorProcessed(investor);
    }
    
    // Fallback implementation - primary indicator is enrichedWith
    return (
      !!investor.enrichedWith ||
      // Secondary indicators (fallbacks) if data was processed but enrichedWith wasn't set
      !!investor.lastRelevanceUpdate || 
      (investor.portfolioCompanies && investor.portfolioCompanies.length > 0) ||
      (investor.keywords && investor.keywords.length > 0) ||
      !!investor.investmentFocus ||
      // Check if the investor has a relevance score
      typeof investor.relevanceScore === 'number'
    );
  };

  // Detect changes in investors array
  useEffect(() => {
    if (investors.length !== lastInvestorsLength) {
      console.log(`Investors array changed from ${lastInvestorsLength} to ${investors.length} items`);
      setLastInvestorsLength(investors.length);
    }
  }, [investors, lastInvestorsLength]);

  // Sync contacts with investors whenever either changes
  useEffect(() => {
    if (investors.length > 0 && contacts.length > 0) {
      const enhancedInvestors = matchContactsToInvestors(investors, contacts);
      setSyncedInvestors(enhancedInvestors);
    } else {
      setSyncedInvestors(investors);
    }
  }, [investors, contacts]);

  // Log processed count
  useEffect(() => {
    const processedCount = investors.filter(checkIfProcessed).length;
    console.log(`InvestorsList - Current processed count: ${processedCount} of ${investors.length}`);
  }, [investors]);

  const {
    searchTerm,
    setSearchTerm,
    sortField,
    sortDirection,
    handleSortChange,
    filteredInvestors,
    processedCount,
    totalCount
  } = useInvestorFiltering(syncedInvestors, "relevanceScore", "desc", checkIfProcessed);

  const handleEnrichInvestor = async (investor: Investor) => {
    if (!onEnrichInvestor) {
      console.error("onEnrichInvestor function not provided");
      return Promise.resolve(investor);
    }

    if (!relevanceContext) {
      toast.error("Please set a relevance context first to enrich investors");
      return Promise.resolve(investor);
    }

    setEnrichingIds(prev => {
      const updated = new Set(prev);
      updated.add(investor.id);
      return updated;
    });

    try {
      console.log(`Starting enrichment for investor ${investor.id} from InvestorsList`);
      const result = await onEnrichInvestor(investor);
      console.log(`Completed enrichment for investor ${investor.id}, saving to database`);
      
      // Explicitly call onInvestorUpdate to ensure the updated investor is persisted
      // This triggers a save to database
      onInvestorUpdate(result);
      
      // Toast success message
      toast.success(`Successfully enriched investor: ${investor.name || investor.firm}`);
      
      return result;
    } catch (error) {
      console.error(`Error enriching investor ${investor.id}:`, error);
      toast.error("Failed to enrich investor. Please try again.");
      return investor;
    } finally {
      setEnrichingIds(prev => {
        const updated = new Set(prev);
        updated.delete(investor.id);
        return updated;
      });
    }
  };

  const isInvestorEnriching = (id: string) => enrichingIds.has(id);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <InvestorsSearchBar 
          searchTerm={searchTerm} 
          setSearchTerm={setSearchTerm} 
        />
        <div className="text-sm text-muted-foreground">
          {processedCount} of {totalCount} processed
        </div>
      </div>

      <InvestorsSortControls 
        sortField={sortField} 
        sortDirection={sortDirection} 
        onSortChange={handleSortChange} 
      />

      <Tabs defaultValue="list" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="list">List View</TabsTrigger>
          <TabsTrigger value="cards">Card View</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <InvestorsListView 
            investors={filteredInvestors} 
            selectedInvestor={selectedInvestor} 
            onSelectInvestor={onSelectInvestor} 
            isEnriching={isEnriching}
            onEnrichInvestor={handleEnrichInvestor}
            isInvestorEnriching={isInvestorEnriching}
            isInvestorProcessed={checkIfProcessed}
          />
        </TabsContent>

        <TabsContent value="cards" className="mt-4">
          <InvestorsCardView 
            investors={filteredInvestors} 
            selectedInvestor={selectedInvestor} 
            onSelectInvestor={onSelectInvestor} 
            isEnriching={isEnriching}
            onEnrichInvestor={handleEnrichInvestor}
            isInvestorEnriching={isInvestorEnriching}
            isInvestorProcessed={checkIfProcessed}
          />
        </TabsContent>
      </Tabs>

      <EnrichmentDebugPanel 
        lastPrompt={enrichmentDebugState.lastPrompt}
        lastResponse={enrichmentDebugState.lastResponse}
        responseTime={enrichmentDebugState.responseTime}
        timestamp={enrichmentDebugState.timestamp}
        model={enrichmentDebugState.model}
      />
    </div>
  );
};

export default InvestorsList;
